/**
 * JournalEntriesSection — dashboard panel for Journal Entries, with its own
 * Company + Period filter (SectionFilter), independent of every other
 * section on the page.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SectionFilter from './SectionFilter';
import VoucherTable from './VoucherTable';
import DrillDownModal from './DrillDownModal';
import { useJournalVouchers } from '../hooks/useDashboard';
import useDrillDown from '../hooks/useDrillDown';
import { computeRangeForFilter } from '../utils/dateFilters';

const PAGE_SIZE = 12;

const fmtCount = (v) => new Intl.NumberFormat('en-IN').format(Number(v) || 0);

const JournalEntriesSection = ({ companies }) => {
  const [companyId, setCompanyId] = useState('all');
  const [dateFilter, setDateFilter] = useState('overall');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const { from, to } = useMemo(() => {
    if (dateFilter === 'custom') return { from: customFrom, to: customTo };
    return computeRangeForFilter(dateFilter);
  }, [dateFilter, customFrom, customTo]);

  const { data: entries, loading, error } = useJournalVouchers(companyId, companies, from, to);
  const isAllCompanies = companyId === 'all';

  const drill = useDrillDown();

  // The list shows one row per journal voucher; Tally shows a journal as its
  // ledger lines. Opening the voucher is what turns one into the other — every
  // line, its group, and the debit/credit split, straight from ledger_entries.
  const handleOpenVoucher = useCallback((row) => {
    drill.openVoucher({
      companyId: row.companyId,
      guid: row.guid,
      label: row.voucherNumber || 'Journal',
    });
  }, [drill]);

  const filteredRows = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    if (!q) return entries || [];
    return (entries || []).filter((r) =>
      String(r.party || '').toLowerCase().includes(q) ||
      String(r.voucherNumber || '').toLowerCase().includes(q) ||
      String(r.narration || '').toLowerCase().includes(q));
  }, [entries, searchTerm]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [companyId, dateFilter, customFrom, customTo, searchTerm]);

  const handleReset = () => {
    setCompanyId('all');
    setDateFilter('overall');
    setCustomFrom('');
    setCustomTo('');
    setSearchTerm('');
    setPage(1);
  };

  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>Journal Entries</h2>
          <p className="panel-subtitle">
            {fmtCount(filteredRows.length)} entrie(s){isAllCompanies ? ' across all companies' : ''}.
          </p>
        </div>
        <input
          type="search"
          className="txn-search"
          placeholder="Search voucher, narration…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <SectionFilter
        idPrefix="journal"
        companies={companies}
        companyId={companyId}
        onCompanyChange={setCompanyId}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        onReset={handleReset}
      />

      <VoucherTable
        rows={pagedRows}
        loading={loading}
        error={error}
        showCompany={isAllCompanies}
        onOpenVoucher={handleOpenVoucher}
      />

      {filteredRows.length > 0 && (
        <div className="pagination-controls">
          <button type="button" className="pagination-button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>
            ← Previous
          </button>
          <span>Page {page} of {pageCount}</span>
          <button type="button" className="pagination-button" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(p + 1, pageCount))}>
            Next →
          </button>
        </div>
      )}

      {drill.isOpen && (
        <DrillDownModal
          stack={drill.stack}
          onPush={drill.push}
          onPopTo={drill.popTo}
          onClose={drill.close}
        />
      )}
    </section>
  );
};

export default JournalEntriesSection;
